import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useRef
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  findOrCreateUser,
  updateUserSelectedRole,
  AuthenticationError
} from '../services/userService'; // Assuming updateUserSelectedRole exists
import {
  AppUser,
  GetUserInput,
  SwitchUserRoleInput,
  UserRole
} from '@/types/userTypes';
import { useLocation, useNavigate } from 'react-router-dom';
import { setCurrentUserForPresence } from '@/services/presenceService';

// Utility function to detect if we're in an Auth0 callback state
const isAuth0Callback = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has('code') && urlParams.has('state');
};

const isPublicStandaloneRoute = (pathname: string): boolean =>
  pathname === '/accept-invitation' ||
  pathname.startsWith('/l/') ||
  pathname.startsWith('/share-link/customer-document/');

interface AppUserContextType {
  appUser: AppUser | null;
  selectedUserRole: UserRole | null;
  isLoading: boolean;
  error: Error | null;
  authError: AuthenticationError | null;
  canRetryAuth: boolean;
  fetchAppUser: () => Promise<void>;
  retryAuthentication: () => Promise<void>;
  setSelectedRole: (role: UserRole | null) => Promise<void>;
}

const AppUserContext = createContext<AppUserContextType | undefined>(undefined);

export const useAppUser = (): AppUserContextType => {
  const context = useContext(AppUserContext);
  if (!context) {
    throw new Error('useAppUser must be used within an AppUserProvider');
  }
  return context;
};

interface AppUserProviderProps {
  children: ReactNode;
}

export const AppUserProvider = ({ children }: AppUserProviderProps) => {
  const {
    user: auth0User,
    isAuthenticated,
    isLoading: isAuth0Loading,
    getAccessTokenSilently,
    loginWithRedirect
  } = useAuth0();

  const [appUser, setAppUser] = useState<AppUser | null>(null);
  const [selectedUserRole, setSelectedUserRoleState] =
    useState<UserRole | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [authError, setAuthError] = useState<AuthenticationError | null>(null);
  const [canRetryAuth, setCanRetryAuth] = useState<boolean>(false);
  const [lastAuthAttempt, setLastAuthAttempt] = useState<number>(0);

  const fetchingUserRef = useRef<boolean>(false);
  const authAttemptCountRef = useRef<number>(0);
  const navigate = useNavigate();
  const location = useLocation();

  // Reset auth attempt count periodically
  useEffect(() => {
    const resetInterval = setInterval(() => {
      authAttemptCountRef.current = 0;
    }, 300000); // Reset every 5 minutes

    return () => clearInterval(resetInterval);
  }, []);

  const resolveSelectedRole = useCallback(
    (user: AppUser | null): UserRole | null => {
      if (!user || !user.userRoles || user.userRoles.length === 0) {
        return null;
      }
      if (user.selectedUserRoleId) {
        return (
          user.userRoles.find((role) => role.id === user.selectedUserRoleId) ||
          null
        );
      }
      // Optional: default to the first role if no specific role is selected yet and roles exist.
      // However, your requirement is to show an empty menu until one is selected.
      // So, if selectedUserRoleId is null, we return null.
      // Consider if a newly created user (with roles but no selectedUserRoleId yet) should auto-select one.
      // For now, strict adherence to "no selectedUserRole, empty menu"
      return null;
    },
    []
  );

  const fetchAppUser = useCallback(
    async (isManualRetry: boolean = false) => {
      // Don't proceed if we're in an Auth0 callback state and Auth0 is still loading
      const inCallback = isAuth0Callback();
      if (inCallback && isAuth0Loading) {
        console.log(
          'In Auth0 callback state, waiting for Auth0 to finish processing...'
        );
        return;
      }

      if (isAuthenticated && auth0User && auth0User.sub) {
        // Prevent excessive auth attempts
        const MAX_AUTH_ATTEMPTS = 3;
        const MIN_RETRY_INTERVAL = 30000; // 30 seconds

        if (!isManualRetry) {
          const now = Date.now();
          const timeSinceLastAttempt = now - lastAuthAttempt;

          if (
            authAttemptCountRef.current >= MAX_AUTH_ATTEMPTS &&
            timeSinceLastAttempt < MIN_RETRY_INTERVAL
          ) {
            setCanRetryAuth(true);
            return;
          }

          if (fetchingUserRef.current) {
            return;
          }
        }

        // Additional delay after Auth0 callback to ensure tokens are properly set
        if (inCallback && !isManualRetry) {
          setTimeout(() => {
            if (!fetchingUserRef.current) {
              fetchAppUser();
            }
          }, 1000); // Wait 1 second for Auth0 to finish processing
          return;
        }

        fetchingUserRef.current = true; // Prevent multiple fetches
        authAttemptCountRef.current += 1;
        setLastAuthAttempt(Date.now());

        setIsLoading(true);
        setError(null);
        setAuthError(null);
        setCanRetryAuth(false);

        try {
          const userInput: GetUserInput = {
            externalId: auth0User.sub,
            email: auth0User.email,
            nickname: auth0User.nickname,
            firstName: auth0User.given_name,
            lastName: auth0User.family_name
          };

          const fetchedUser = await findOrCreateUser(
            userInput,
            getAccessTokenSilently,
            loginWithRedirect
          );

          console.log('Fetched user:', fetchedUser);
          setAppUser(fetchedUser);
          if (fetchedUser) {
            setSelectedUserRoleState(resolveSelectedRole(fetchedUser));
            
            // Set user info for presence tracking
            const displayName = `${fetchedUser.firstName || ''} ${fetchedUser.lastName || ''}`.trim() || 
                               auth0User.nickname || 
                               auth0User.email || 
                               'Anonymous';
            setCurrentUserForPresence(
              fetchedUser.id,
              displayName,
              fetchedUser.avatarUrl || auth0User.picture || null
            );
          } else {
            setSelectedUserRoleState(null);
          }

          // Reset auth attempt count on success
          authAttemptCountRef.current = 0;
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : 'An unknown error occurred';

          if (errorMessage === 'REDIRECTING_TO_LOGIN') {
            console.log('Redirecting to login, suppressing error handling...');
            return;
          }

          console.error('Failed to fetch or create app user:', err);

          // Try to extract authentication error type from error message
          let detectedAuthError: AuthenticationError | null = null;
          if (errorMessage.includes('POPUP_BLOCKED')) {
            detectedAuthError = AuthenticationError.POPUP_BLOCKED;
          } else if (errorMessage.includes('CONSENT_REQUIRED')) {
            detectedAuthError = AuthenticationError.CONSENT_REQUIRED;
          } else if (errorMessage.includes('LOGIN_REQUIRED')) {
            detectedAuthError = AuthenticationError.LOGIN_REQUIRED;
          } else if (errorMessage.includes('NETWORK_ERROR')) {
            detectedAuthError = AuthenticationError.NETWORK_ERROR;
          }

          setAuthError(detectedAuthError);
          setError(
            err instanceof Error ? err : new Error('An unknown error occurred')
          );

          // Allow retry for certain error types or after max attempts reached
          const shouldAllowRetry =
            detectedAuthError === AuthenticationError.POPUP_BLOCKED ||
            detectedAuthError === AuthenticationError.NETWORK_ERROR ||
            authAttemptCountRef.current >= MAX_AUTH_ATTEMPTS;
          setCanRetryAuth(shouldAllowRetry);
        } finally {
          setIsLoading(false);
          fetchingUserRef.current = false;
        }
      } else if (!isAuth0Loading && !isAuthenticated) {
        // Not authenticated and Auth0 is not loading, so clear user state
        setAppUser(null);
        setSelectedUserRoleState(null);
        setAuthError(null);
        setCanRetryAuth(false);
        setIsLoading(false);
        if (!isPublicStandaloneRoute(location.pathname)) {
          navigate('/'); // Redirect to logged out page
        }
      }
    },
    [
      isAuthenticated,
      auth0User,
      getAccessTokenSilently,
      loginWithRedirect,
      isAuth0Loading,
      resolveSelectedRole,
      lastAuthAttempt,
      location.pathname,
      navigate
    ]
  );

  const retryAuthentication = useCallback(async () => {
    console.log('Manual authentication retry requested');
    await fetchAppUser(true);
  }, [fetchAppUser]);

  useEffect(() => {
    fetchAppUser();
  }, [fetchAppUser]); // fetchAppUser is memoized with useCallback

  const setSelectedRole = async (role: UserRole | null) => {
    if (!appUser) return; // Should not happen if called appropriately

    setIsLoading(true);
    try {
      if (role) {
        const switchRoleInput: SwitchUserRoleInput = {
          userId: appUser.id,
          userRoleId: role.id
        };
        // Persist to backend (assuming you have implemented updateUserSelectedRole and its backend endpoint)
        // Make sure appUser.id is the backend GUID
        await updateUserSelectedRole(
          switchRoleInput,
          getAccessTokenSilently,
          loginWithRedirect
        );
        // Update local state
        setAppUser((prevUser) =>
          prevUser ? { ...prevUser, selectedUserRoleId: role.id } : null
        );
        setSelectedUserRoleState(role);
      } else {
        // If clearing a role, you might need a backend call to set SelectedUserRoleId to null
        // For now, just updating client state:
        setAppUser((prevUser) =>
          prevUser ? { ...prevUser, selectedUserRoleId: null } : null
        );
        setSelectedUserRoleState(null);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'An unknown error occurred';
      if (errorMessage === 'REDIRECTING_TO_LOGIN') {
        console.log('Redirecting to login, suppressing error handling...');
        return;
      }
      console.error('Failed to set selected role:', err);
      setError(err instanceof Error ? err : new Error('Failed to update role'));
      // Potentially revert optimistic update or re-fetch user
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AppUserContext.Provider
      value={{
        appUser,
        selectedUserRole,
        isLoading,
        error,
        authError,
        canRetryAuth,
        fetchAppUser,
        retryAuthentication,
        setSelectedRole
      }}
    >
      {children}
    </AppUserContext.Provider>
  );
};
